import { type FC } from 'react';
import {
  type BaseProps,
  type ComponentShadow,
  type ComponentAppearance,
  type ComponentVariant,
} from 'src/modules/shared/types/component.type';
import { getDisabledClasses } from 'src/modules/shared/styles/component.styles';
import {
  cardBaseClasses,
  cardSizeClasses,
  cardOutlineClasses,
  cardSoftClasses,
  cardFilledClasses,
  cardGhostClasses,
  cardHighlightClasses,
  cardHoverClasses,
  cardActiveClasses,
} from './styles';

export type CardProps = BaseProps & {
  onClick?: (e?: React.MouseEvent) => void;
  isHighlighted?: boolean;
  shadow?: ComponentShadow;
  role?: string;
  'aria-checked'?: boolean;
  tabIndex?: number;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  appearance?: ComponentAppearance;
};

const appearanceClassMap: Record<
  NonNullable<ComponentAppearance>,
  Record<ComponentVariant, string>
> = {
  filled: cardFilledClasses,
  outline: cardOutlineClasses,
  soft: cardSoftClasses,
  ghost: cardGhostClasses,
};

export const Card: FC<CardProps> = ({
  children,
  className = '',
  variant = 'secondary',
  size = 'md',
  disabled = false,
  onClick,
  isHighlighted = false,
  shadow = 'flat',
  role,
  'aria-checked': ariaChecked,
  tabIndex,
  onKeyDown,
  appearance = 'filled',
}) => {
  const handleClick = (e: React.MouseEvent) => {
    if (!disabled && onClick) {
      onClick(e);
    }
  };

  const isInteractive = !disabled && onClick;

  const baseClasses = [
    cardBaseClasses,
    cardSizeClasses[size],
    getDisabledClasses(disabled),
    isInteractive ? 'cursor-pointer' : '',
  ];

  const appearanceClasses = [];
  if (isHighlighted) {
    const highlightClass = cardHighlightClasses[appearance][variant] || '';
    appearanceClasses.push(highlightClass);
  } else {
    const baseAppearanceClass = appearanceClassMap[appearance][variant] || '';
    appearanceClasses.push(baseAppearanceClass);
  }

  const interactiveClasses = [];
  if (isInteractive) {
    interactiveClasses.push(cardHoverClasses[variant]);
    interactiveClasses.push(cardActiveClasses[variant]);
  }

  const shadowMap: Record<ComponentShadow, string> = {
    'inset-sm': 'shadow-sm',
    inset: 'shadow-md',
    flat: 'shadow-md',
    none: '',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl',
    '2xl': 'shadow-2xl',
    inner: 'shadow-inner',
  };
  const shadowClass = shadow in shadowMap ? shadowMap[shadow] : '';

  const allClasses = [
    ...baseClasses,
    ...appearanceClasses,
    ...interactiveClasses,
    shadowClass,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      onClick={handleClick}
      className={allClasses}
      role={role}
      aria-checked={ariaChecked}
      tabIndex={tabIndex}
      onKeyDown={onKeyDown}
    >
      {children}
    </div>
  );
};
